/**
 * Client service for Hospital Light Patient Directory (C8.a / C8.b).
 * Plan docs/plans/sector-operations.md §2 SO-D18, SO-D20, SO-P15, SO-P255.
 * Wraps callables via callable(); components never import firebase/*.
 */

import { callable } from './callable'
import type {
  HosPatientDoc,
  HosVisitDoc,
  HosConsentDoc,
  HosPatientSettingsDoc,
  CreateHosPatientInput,
  UpdateHosPatientInput,
  CheckInHosVisitInput,
  CheckOutHosVisitInput,
  RecordHosConsentInput,
  WithdrawHosConsentInput,
  SetHosPatientDirectoryEnabledInput,
  SetHosPatientNoticeInput,
  ListHosPatientsInput,
  GetHosPatientInput,
  GetHosPatientSettingsInput,
} from '../../functions-common/src/ops/hosPatients'

export type {
  HosPatientDoc,
  HosVisitDoc,
  HosConsentDoc,
  HosPatientSettingsDoc,
  CreateHosPatientInput,
  UpdateHosPatientInput,
  CheckInHosVisitInput,
  CheckOutHosVisitInput,
  RecordHosConsentInput,
  WithdrawHosConsentInput,
  SetHosPatientDirectoryEnabledInput,
  SetHosPatientNoticeInput,
  ListHosPatientsInput,
  GetHosPatientInput,
  GetHosPatientSettingsInput,
}

export interface CreateHosPatientResult {
  patientId: string
  pseudoRef: string
}

export interface UpdateHosPatientResult {
  success: boolean
  patientId: string
}

export interface CheckInHosVisitResult {
  visitId: string
}

export interface CheckOutHosVisitResult {
  success: boolean
}

export interface RecordHosConsentResult {
  consentId: string
}

export interface WithdrawHosConsentResult {
  success: boolean
}

export interface SetHosPatientDirectoryEnabledResult {
  success: boolean
  enabled: boolean
}

export interface SetHosPatientNoticeResult {
  success: boolean
  noticeVersion: string
}

export interface ListHosPatientsResult {
  items: HosPatientDoc[]
}

export interface GetHosPatientResult {
  patient: HosPatientDoc
  visits: HosVisitDoc[]
  consents: HosConsentDoc[]
}

export async function getHosPatientSettings(
  input: GetHosPatientSettingsInput,
): Promise<HosPatientSettingsDoc> {
  return callable<GetHosPatientSettingsInput, HosPatientSettingsDoc>('getHosPatientSettings', input)
}

export async function setHosPatientDirectoryEnabled(
  input: SetHosPatientDirectoryEnabledInput,
): Promise<SetHosPatientDirectoryEnabledResult> {
  return callable<SetHosPatientDirectoryEnabledInput, SetHosPatientDirectoryEnabledResult>(
    'setHosPatientDirectoryEnabled',
    input,
  )
}

export async function setHosPatientNotice(
  input: SetHosPatientNoticeInput,
): Promise<SetHosPatientNoticeResult> {
  return callable<SetHosPatientNoticeInput, SetHosPatientNoticeResult>('setHosPatientNotice', input)
}

export async function createHosPatient(
  input: CreateHosPatientInput,
): Promise<CreateHosPatientResult> {
  return callable<CreateHosPatientInput, CreateHosPatientResult>('createHosPatient', input)
}

export async function updateHosPatient(
  input: UpdateHosPatientInput,
): Promise<UpdateHosPatientResult> {
  return callable<UpdateHosPatientInput, UpdateHosPatientResult>('updateHosPatient', input)
}

export async function recordHosConsent(
  input: RecordHosConsentInput,
): Promise<RecordHosConsentResult> {
  return callable<RecordHosConsentInput, RecordHosConsentResult>('recordHosConsent', input)
}

export async function withdrawHosConsent(
  input: WithdrawHosConsentInput,
): Promise<WithdrawHosConsentResult> {
  return callable<WithdrawHosConsentInput, WithdrawHosConsentResult>('withdrawHosConsent', input)
}

export async function checkInHosVisit(
  input: CheckInHosVisitInput,
): Promise<CheckInHosVisitResult> {
  return callable<CheckInHosVisitInput, CheckInHosVisitResult>('checkInHosVisit', input)
}

export async function checkOutHosVisit(
  input: CheckOutHosVisitInput,
): Promise<CheckOutHosVisitResult> {
  return callable<CheckOutHosVisitInput, CheckOutHosVisitResult>('checkOutHosVisit', input)
}

export async function listHosPatients(
  input: ListHosPatientsInput,
): Promise<ListHosPatientsResult> {
  return callable<ListHosPatientsInput, ListHosPatientsResult>('listHosPatients', input)
}

export async function getHosPatient(
  input: GetHosPatientInput,
): Promise<GetHosPatientResult> {
  return callable<GetHosPatientInput, GetHosPatientResult>('getHosPatient', input)
}
